/**
 * reportStaleTime — ADR-RE-7 layer 4 (Story 37.8)
 *
 * Derives the React Query `staleTime` (in ms) for the editor's whole-report
 * live-data query (`useReportData`), so that revisiting a date range within its
 * platforms' soft-TTL window serves from cache and issues NO network request.
 *
 * Rule (corrections #4 + #12): the query spans MULTIPLE platforms with DIFFERENT
 * soft TTLs, so `staleTime` is the **minimum** soft TTL across the platforms
 * present in the current widget set — the tightest-freshness platform wins, so
 * no platform's data is ever served as "fresh" past ITS own soft TTL.
 *
 * Client/server divergence is intentional and conservative-by-design: the server
 * resolves a widget's platform from `config.integrationId`, from empty-string
 * sentinels, AND from `resolve:<platform>` cross-platform keys
 * (`widget-populate.ts`). The client can only resolve a literal slug, so it
 * UNDER-resolves sentinel/cross-platform widgets. To never over-extend freshness
 * for an unresolved platform, an empty platform set falls back to the TIGHTEST
 * known TTL (GA4 = 1h), NEVER the global 5-min default and NEVER the 7-day hard
 * expiry (`STALE_EXPIRATION_TTL`) — which would mask legitimately-refreshed
 * server data.
 */

import { PLATFORM_CACHE_TTL } from "@provenleap/integrations";

/**
 * The tightest known soft TTL (GA4 = 3600s) in milliseconds. Used as the
 * fallback when NO platform resolves in the widget set, so an unresolved
 * sentinel/cross-platform/content widget can never over-extend freshness.
 */
export const GA4_TIGHTEST_TTL_MS = (PLATFORM_CACHE_TTL["google-analytics-4"] ?? 3600) * 1000;

/** A `resolve:<platform>` sentinel the server (not the client) expands. */
function isResolverSentinel(slug: string): boolean {
  return slug.startsWith("resolve:");
}

/**
 * Collect a widget's candidate platform slugs from BOTH its top-level
 * `config.integrationId` AND its per-metric `config.metrics[].integrationId`
 * (multi-metric widgets like data-table seed platforms per-metric, not always at
 * the top level). `config` is `Record<string, unknown>` — every field is treated
 * as `unknown` and only non-empty, non-sentinel strings are kept.
 */
function collectWidgetPlatformSlugs(config: Record<string, unknown>): string[] {
  const slugs: string[] = [];

  const addSlug = (candidate: unknown): void => {
    if (typeof candidate !== "string") return;
    const slug = candidate.trim();
    if (!slug || isResolverSentinel(slug)) return;
    slugs.push(slug);
  };

  addSlug(config.integrationId);

  const metrics = config.metrics;
  if (Array.isArray(metrics)) {
    for (const entry of metrics) {
      if (entry && typeof entry === "object") {
        addSlug((entry as { integrationId?: unknown }).integrationId);
      }
    }
  }

  return slugs;
}

/**
 * Derives the live-data query's `staleTime` (ms) = MIN soft TTL across the
 * platforms present in `widgets`. Falls back to {@link GA4_TIGHTEST_TTL_MS} when
 * no platform resolves. Never returns the 7-day hard expiry.
 *
 * @param widgets the widget set on the active canvas (each widget's `config` may
 *   carry an `integrationId` slug and/or per-metric `metrics[].integrationId`).
 */
export function deriveStaleTimeMs(
  widgets: ReadonlyArray<{ config: Record<string, unknown> }>,
): number {
  let minSoftTtlSec: number | null = null;

  for (const widget of widgets) {
    for (const slug of collectWidgetPlatformSlugs(widget.config)) {
      const ttlSec = PLATFORM_CACHE_TTL[slug];
      if (typeof ttlSec !== "number") continue; // unknown slug — skip, never over-extend
      minSoftTtlSec = minSoftTtlSec === null ? ttlSec : Math.min(minSoftTtlSec, ttlSec);
    }
  }

  if (minSoftTtlSec === null) return GA4_TIGHTEST_TTL_MS;
  return minSoftTtlSec * 1000;
}
